import { Link, createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import { OtpInput } from "@/components/auth/OtpInput";
import { PhoneField, type PhoneFieldValue } from "@/components/auth/PhoneField";
import { Button } from "@/components/ui/button";
import { ApiError, authApi, persistSession } from "@/lib/api";
import { defaultCountryFromDevice } from "@/lib/countries";
import { useOnboardingStore } from "@/stores/onboarding";

function phoneFieldFromE164(phone: string, fallbackCountry: CountryCode): PhoneFieldValue {
  const parsed = parsePhoneNumberFromString(phone);
  if (parsed?.country) {
    return {
      e164: parsed.number,
      national: parsed.formatNational(),
      country: parsed.country,
      valid: parsed.isValid(),
    };
  }
  return {
    e164: phone,
    national: phone.replace(/^\+\d{1,3}/, ""),
    country: fallbackCountry,
    valid: Boolean(phone),
  };
}

const RESEND_SECONDS = 30;

type VerifyLocationState = {
  phone?: string;
  country?: CountryCode;
  name?: string;
  inviteCode?: string;
  devOtp?: string;
};

export const Route = createFileRoute("/onboarding/verify")({
  head: () => ({
    meta: [{ title: "Verify phone" }],
  }),
  component: VerifyStep,
});

function otpErrorMessage(err: ApiError): string {
  if (err.code === "OTP_EXPIRED" || /expired/i.test(err.message)) {
    return "That code has expired. Request a new one.";
  }
  if (err.code === "INVALID_OTP" || /invalid/i.test(err.message)) {
    return "That code is incorrect. Check the SMS and try again.";
  }
  if (err.code === "PHONE_IN_USE") {
    return "This phone number already has an account.";
  }
  return err.message || "Something went wrong. Please try again.";
}

function VerifyStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locationState = useRouterState({
    select: (s) => (s.location.state as VerifyLocationState | undefined) ?? {},
  });

  const storePhone = useOnboardingStore((s) => s.phone);
  const storeCountry = useOnboardingStore((s) => s.country);
  const storeName = useOnboardingStore((s) => s.name);
  const storeInvite = useOnboardingStore((s) => s.inviteCode);
  const setPhone = useOnboardingStore((s) => s.setPhone);
  const setCountry = useOnboardingStore((s) => s.setCountry);
  const setStep = useOnboardingStore((s) => s.setStep);

  const initialPhone = locationState.phone || storePhone;
  const initialCountry = (locationState.country ||
    storeCountry ||
    defaultCountryFromDevice()) as CountryCode;
  const name = locationState.name || storeName;
  const inviteCode = locationState.inviteCode || storeInvite || "";

  const [editingPhone, setEditingPhone] = useState(!initialPhone);
  const [phoneValue, setPhoneValue] = useState<PhoneFieldValue>(() =>
    initialPhone
      ? phoneFieldFromE164(initialPhone, initialCountry)
      : {
          e164: "",
          national: "",
          country: initialCountry,
          valid: false,
        },
  );
  const [otp, setOtp] = useState(locationState.devOtp || "");
  const [devOtp, setDevOtp] = useState<string | null>(locationState.devOtp ?? null);
  const [sent, setSent] = useState(Boolean(initialPhone));
  const [error, setError] = useState<string | null>(null);
  const [phoneInUse, setPhoneInUse] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(initialPhone ? RESEND_SECONDS : 0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => {
      setCooldown((c) => Math.max(0, c - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  const requestCode = useCallback(
    async (phone = phoneValue.e164, country = phoneValue.country) => {
      setError(null);
      setPhoneInUse(false);
      if (!phone) {
        setError("Enter a valid phone number.");
        return;
      }
      setLoading(true);
      try {
        const result = await authApi.requestOtp({
          phone,
          country,
          channel: "sms",
        });
        setPhone(phone);
        setCountry(country);
        setSent(true);
        setEditingPhone(false);
        setCooldown(RESEND_SECONDS);
        setDevOtp(result.devOtp ?? null);
        if (result.devOtp) setOtp(result.devOtp);
      } catch (err) {
        if (err instanceof ApiError && err.code === "PHONE_IN_USE") {
          setPhoneInUse(true);
          setError(otpErrorMessage(err));
        } else {
          setError(err instanceof ApiError ? otpErrorMessage(err) : t("common.errorGeneric"));
        }
      } finally {
        setLoading(false);
      }
    },
    [phoneValue.country, phoneValue.e164, setCountry, setPhone, t],
  );

  useEffect(() => {
    if (initialPhone && sent && !locationState.devOtp) {
      // Code was already requested on signup; start cooldown only.
      setCooldown(RESEND_SECONDS);
    }
    // Intentionally once on mount for signup→verify handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onVerify() {
    setError(null);
    setPhoneInUse(false);
    if (otp.length !== 6) {
      setError("Enter the 6-digit code from your SMS.");
      return;
    }
    setLoading(true);
    try {
      const payload: Parameters<typeof authApi.verifyOtp>[0] = {
        phone: phoneValue.e164 || storePhone,
        otp,
        country: phoneValue.country,
        channel: "sms",
      };
      if (name) payload.name = name;
      if (inviteCode) payload.inviteCode = inviteCode;
      const result = await authApi.verifyOtp(payload);
      persistSession("user", {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken ?? null,
      });
      setStep("persona");
      window.location.assign(result.dashboard || "/onboarding/persona");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "PHONE_IN_USE") setPhoneInUse(true);
        setError(otpErrorMessage(err));
      } else {
        setError(t("common.errorGeneric"));
      }
    } finally {
      setLoading(false);
    }
  }

  const displayPhone = phoneValue.e164 || storePhone;

  return (
    <div className="flex flex-1 flex-col">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
        {t("onboarding.verify.eyebrow")}
      </p>
      <h1 className="font-display text-3xl font-bold">{t("onboarding.verify.title")}</h1>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        {sent && !editingPhone
          ? t("onboarding.verify.body", { phone: displayPhone })
          : "Enter your phone number to receive a 6-digit code."}
      </p>

      <div className="mt-8 space-y-4">
        {editingPhone ? (
          <>
            <PhoneField
              required
              value={phoneValue}
              onChange={(v) => {
                setError(null);
                setPhoneValue(v);
              }}
              label={t("common.phone")}
            />
            <Button
              type="button"
              className="w-full"
              disabled={loading || !phoneValue.valid}
              onClick={() => void requestCode()}
            >
              {loading ? t("common.loading") : t("onboarding.verify.send")}
            </Button>
          </>
        ) : (
          <>
            <OtpInput
              value={otp}
              onChange={(v) => {
                setError(null);
                setOtp(v);
              }}
              autoFocus
              error={Boolean(error)}
              disabled={loading}
            />

            {devOtp ? (
              <p className="rounded-md border border-primary/20 bg-primary-soft px-3 py-2 text-xs">
                {t("onboarding.verify.devOtp")}:{" "}
                <span className="font-mono font-bold">{devOtp}</span>
              </p>
            ) : null}

            {error ? (
              <p className="rounded-md border border-destructive/25 bg-severe-soft px-3 py-2 text-xs text-severe">
                {error}
                {phoneInUse ? (
                  <>
                    {" "}
                    <Link to="/login" className="font-semibold underline">
                      Sign in
                    </Link>
                  </>
                ) : null}
              </p>
            ) : null}

            <Button
              type="button"
              className="w-full"
              disabled={loading || otp.length !== 6}
              onClick={() => void onVerify()}
            >
              {loading ? t("common.loading") : t("onboarding.verify.cta")}
            </Button>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                disabled={loading || cooldown > 0}
                onClick={() => void requestCode()}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : t("onboarding.verify.resend")}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={loading}
                onClick={() => {
                  setEditingPhone(true);
                  setOtp("");
                  setError(null);
                  setSent(false);
                }}
              >
                Change number
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="mt-auto pt-8">
        <Button
          variant="outline"
          className="w-full"
          onClick={() => void navigate({ to: "/signup" })}
        >
          {t("common.back")}
        </Button>
      </div>
    </div>
  );
}
